import { useCallback, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDispatch } from 'react-redux'
import { forEach, includes, isEmpty, isNil, join, size } from 'lodash'

import { COMMON_AVAILABLE_HOTKEYS } from '../constants'

const CSS_CLASSES = {
  SELECTED_ROW: 'selected-row',
  SELECTED_CELL: 'selected-cell',
  BORDER_HIGHLIGHT: 'border-highlight',
}

const ARROW_KEYS = {
  UP: 'ArrowUp',
  DOWN: 'ArrowDown',
  LEFT: 'ArrowLeft',
  RIGHT: 'ArrowRight',
}

const useRowHighlight = ({
  tableSelector,
  rowsSelector,
  scrollContainerSelector,
  cellsSelector,
  areHotkeysDisabled,
  isCellSelectionDisabled,
  isRowSelectionDisabled,
  updateHotkeysAction,
}) => {
  const dispatch = useDispatch()
  const [highlightIndex, setHighlightIndex] = useState(null)
  const [maxHighlightIndex, setMaxHighlightIndex] = useState(null)
  const [cellHighlightIndex, setCellHighlightIndex] = useState(null)
  const [maxCellHighlightIndex, setCellMaxHighlightIndex] = useState(null)
  const tableRef = useRef()
  const rowsRef = useRef()
  const scrollRef = useRef()
  const cellsRef = useRef()
  const highlightedCellRef = useRef()
  const highlightedRowRef = useRef()

  const highlightRowAtIndex = useCallback(
    rowIndex => {
      if (isRowSelectionDisabled || isNil(rowIndex)) {
        dispatch(updateHotkeysAction({ selectedRowKey: null }))
        return
      }

      highlightedRowRef.current?.classList.remove(CSS_CLASSES.SELECTED_ROW)
      highlightedRowRef.current?.classList.remove(CSS_CLASSES.BORDER_HIGHLIGHT)
      highlightedRowRef.current = rowsRef.current[rowIndex]
      highlightedRowRef.current?.classList.add(CSS_CLASSES.SELECTED_ROW)
      if (isCellSelectionDisabled) {
        highlightedRowRef.current?.classList.add(CSS_CLASSES.BORDER_HIGHLIGHT)
      }

      // set default highlighted cell
      cellsRef.current =
        highlightedRowRef.current?.querySelectorAll(cellsSelector)

      setCellMaxHighlightIndex((size(cellsRef?.current) || 1) - 1)

      dispatch(
        updateHotkeysAction({
          selectedRowKey: highlightedRowRef.current?.dataset.rowKey,
        }),
      )
    },
    [
      cellsSelector,
      dispatch,
      isCellSelectionDisabled,
      isRowSelectionDisabled,
      updateHotkeysAction,
    ],
  )

  const highlightCellAtIndex = useCallback(
    cellIndex => {
      if (
        isCellSelectionDisabled ||
        isRowSelectionDisabled ||
        isNil(cellIndex) ||
        isEmpty(cellsRef.current)
      ) {
        dispatch(updateHotkeysAction({ selectedCellKey: null }))
        return
      }

      highlightedCellRef.current?.classList.remove(CSS_CLASSES.SELECTED_CELL)
      highlightedCellRef.current = cellsRef.current[cellIndex]
      highlightedCellRef.current?.classList.add(CSS_CLASSES.SELECTED_CELL)

      const classList = highlightedCellRef.current?.classList || []
      let key
      forEach(classList, className => {
        if (className.indexOf('field-') === 0) {
          const splits = className.split('-')
          // eslint-disable-next-line prefer-destructuring
          key = splits[1]
        }
      })

      if (key) {
        dispatch(updateHotkeysAction({ selectedCellKey: key }))
      }
    },
    [
      dispatch,
      isCellSelectionDisabled,
      isRowSelectionDisabled,
      updateHotkeysAction,
    ],
  )

  const incrementHighlightIndex = useCallback(
    (isCell = false, goToEnd = false) => {
      const maxIndex = isCell ? maxCellHighlightIndex : maxHighlightIndex
      const prevIndex = isCell ? cellHighlightIndex : highlightIndex
      let newIndex

      if (goToEnd) {
        newIndex = maxIndex
      } else {
        newIndex = prevIndex < maxIndex ? prevIndex + 1 : 0
      }

      if (isCell) {
        setCellHighlightIndex(newIndex)
      } else {
        setHighlightIndex(newIndex)
      }
    },
    [
      cellHighlightIndex,
      highlightIndex,
      maxCellHighlightIndex,
      maxHighlightIndex,
    ],
  )

  const decrementHighlightIndex = useCallback(
    (isCell = false, goToStart = false) => {
      const maxIndex = isCell ? maxCellHighlightIndex : maxHighlightIndex
      const prevIndex = isCell ? cellHighlightIndex : highlightIndex
      let newIndex

      if (goToStart) {
        newIndex = 0
      } else {
        newIndex = prevIndex > 0 ? prevIndex - 1 : maxIndex
      }

      if (isCell) {
        setCellHighlightIndex(newIndex)
      } else {
        setHighlightIndex(newIndex)
      }
    },
    [
      cellHighlightIndex,
      highlightIndex,
      maxCellHighlightIndex,
      maxHighlightIndex,
    ],
  )

  const shouldIgnoreArrowEvent = useCallback(
    event =>
      areHotkeysDisabled ||
      event.target instanceof HTMLInputElement ||
      isRowSelectionDisabled ||
      (includes([ARROW_KEYS.LEFT, ARROW_KEYS.RIGHT], event.key) &&
        isCellSelectionDisabled),
    [areHotkeysDisabled, isRowSelectionDisabled, isCellSelectionDisabled],
  )

  useHotkeys(
    join(
      [
        COMMON_AVAILABLE_HOTKEYS.UP.hotkey,
        COMMON_AVAILABLE_HOTKEYS.DOWN.hotkey,
        COMMON_AVAILABLE_HOTKEYS.LEFT.hotkey,
        COMMON_AVAILABLE_HOTKEYS.RIGHT.hotkey,
        COMMON_AVAILABLE_HOTKEYS.SHIFT_UP.hotkey,
        COMMON_AVAILABLE_HOTKEYS.SHIFT_DOWN.hotkey,
        COMMON_AVAILABLE_HOTKEYS.SHIFT_LEFT.hotkey,
        COMMON_AVAILABLE_HOTKEYS.SHIFT_RIGHT.hotkey,
      ],
      ',',
    ),
    event => {
      if (shouldIgnoreArrowEvent(event)) {
        return
      }

      if (event.key === ARROW_KEYS.UP) {
        decrementHighlightIndex(false, event.shiftKey)
      }
      if (event.key === ARROW_KEYS.DOWN) {
        incrementHighlightIndex(false, event.shiftKey)
      }
      if (event.key === ARROW_KEYS.LEFT) {
        decrementHighlightIndex(true, event.shiftKey)
      }
      if (event.key === ARROW_KEYS.RIGHT) {
        incrementHighlightIndex(true, event.shiftKey)
      }

      if (!isEmpty(rowsRef.current)) {
        event.preventDefault()
      }
    },
    [
      shouldIgnoreArrowEvent,
      decrementHighlightIndex,
      incrementHighlightIndex,
      isRowSelectionDisabled,
    ],
  )

  useEffect(() => {
    if (isEmpty(rowsRef.current) || isNil(highlightIndex)) {
      return
    }

    tableRef.current?.scrollIntoView()
    const currentRow = rowsRef.current[highlightIndex]
    const containerHeight = scrollRef.current?.offsetHeight
    const containerWidth = scrollRef.current?.offsetWidth

    let offsetPositionX
    if (!isEmpty(cellsRef.current) && !isNil(cellHighlightIndex)) {
      const currentCell = cellsRef.current[cellHighlightIndex]
      offsetPositionX = currentCell.offsetLeft - containerWidth / 2
    }
    const offsetPositionY = currentRow.offsetTop - containerHeight / 2

    scrollRef.current?.scrollTo(offsetPositionX, offsetPositionY)
  }, [highlightIndex, rowsRef, cellsRef, cellHighlightIndex])

  const initHighlight = useCallback(() => {
    tableRef.current = document.querySelector(tableSelector)
    ;[scrollRef.current] = document.querySelectorAll(scrollContainerSelector)
    rowsRef.current = document.querySelectorAll(rowsSelector)
    setMaxHighlightIndex(size(rowsRef.current) - 1)

    if (!isRowSelectionDisabled) {
      setHighlightIndex(0)
      highlightRowAtIndex(0)

      if (!isCellSelectionDisabled) {
        setCellHighlightIndex(0)
        highlightCellAtIndex(0)
      }
    }
  }, [
    isCellSelectionDisabled,
    isRowSelectionDisabled,
    highlightCellAtIndex,
    highlightRowAtIndex,
    rowsSelector,
    scrollContainerSelector,
    tableSelector,
  ])

  useEffect(() => {
    highlightRowAtIndex(highlightIndex)
  }, [highlightIndex, highlightRowAtIndex])

  useEffect(() => {
    highlightCellAtIndex(cellHighlightIndex)
  }, [highlightIndex, cellHighlightIndex, highlightCellAtIndex])

  useEffect(() => {
    if (isCellSelectionDisabled || isRowSelectionDisabled) {
      setCellHighlightIndex(null)

      if (!isEmpty(highlightedCellRef.current)) {
        highlightedCellRef.current.classList.remove(CSS_CLASSES.SELECTED_CELL)
      }
    }
  }, [isCellSelectionDisabled, isRowSelectionDisabled, highlightedCellRef])

  useEffect(() => {
    if (isRowSelectionDisabled) {
      setHighlightIndex(null)

      if (!isEmpty(highlightedRowRef.current)) {
        highlightedRowRef.current.classList.remove(CSS_CLASSES.SELECTED_ROW)
        highlightedRowRef.current.classList.remove(CSS_CLASSES.BORDER_HIGHLIGHT)
      }
    }
  }, [isRowSelectionDisabled, highlightedRowRef])

  return {
    highlightIndex,
    initHighlight,
  }
}

export default useRowHighlight
